import { useState } from 'react';

/**
 * A hook that syncs state with sessionStorage.
 * State persists during browser session but clears on tab close.
 *
 * @param key - The sessionStorage key
 * @param initialValue - The initial value if key doesn't exist
 * @returns [value, setValue] tuple like useState
 */
export function useSessionStorage<T>(key: string, initialValue: T): [T, (value: T) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    // SSR safety check
    if (typeof window === 'undefined') {
      return initialValue;
    }

    try {
      const item = window.sessionStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error('Error reading from sessionStorage:', error);
      return initialValue;
    }
  });

  const setValue = (value: T) => {
    try {
      setStoredValue(value);
      // SSR safety check
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(key, JSON.stringify(value));
      }
    } catch (error) {
      console.error('Error saving to sessionStorage:', error);
    }
  };

  return [storedValue, setValue];
}
